import type React from "react";
import clsx from "clsx";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id?: string;
  name?: string;
  placeholder?: string;
  className?: string;
  min?: string | number | undefined;
  max?: string | number | undefined;
  step?: number;
  disabled?: boolean;
  success?: boolean;
  error?: boolean;
  hint?: string;
  errorMessage?: string;
  sizeType?: "xs" | "sm" | "md" | "lg";
  ref?: React.Ref<HTMLInputElement>;
}

const Input = ({
  type = "text",
  id,
  name,
  placeholder,
  className = "",
  min,
  max,
  step,
  disabled = false,
  success = false,
  error = false,
  hint,
  errorMessage,
  sizeType = "md",
  ref,
  ...rest
}: InputProps) => {
  const baseClasses =
    "w-full rounded-sm border appearance-none placeholder:text-gray-400 focus:outline-hidden focus:border-blue-400";

  const stateClasses = disabled
    ? "text-gray-500 border-gray-300 bg-gray-100 cursor-not-allowed opacity-40"
    : error
      ? "border-error-500 focus:border-error-300 focus:ring-error-500/20"
      : success
        ? "border-success-500 focus:border-success-300 focus:ring-success-500/20"
        : "bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20";

  const sizeClasses = {
    xs: "h-8 text-xs px-1 py-1.5",
    sm: "h-9 text-sm px-2 py-2",
    md: "h-11 text-sm px-3 py-2.5",
    lg: "h-12 text-base px-3 py-3",
  }[sizeType];

  return (
    <>
      <input
        ref={ref}
        id={id}
        type={type}
        disabled={disabled}
        name={name}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        className={clsx(baseClasses, stateClasses, sizeClasses, className)}
        {...rest}
      />

      {errorMessage ? (
        <p className="mt-1.5 text-xs text-red-600">{errorMessage}</p>
      ) : hint ? (
        <p
          className={`mt-1.5 text-xs ${
            success ? "text-success-500" : "text-gray-500"
          }`}
        >
          {hint}
        </p>
      ) : null}
    </>
  );
};

Input.displayName = "Input";

export default Input;
